import { ReactNode, useEffect, useState, useRef } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useServerSideAuth, isPwaMode } from "@/utils/pwaUtils";
import {
  validateAuthToken,
  shouldUsePwaTokenAuth,
  establishSessionFromMagicLink,
  clearAuthToken,
  getAuthToken
} from "@/utils/auth/tokenAuthUtils";
import { generateDeviceFingerprint } from "@/utils/deviceFingerprint";
import { startTokenValidityChecker, stopTokenValidityChecker } from "@/hooks/useTokenAuth";
import { logger } from "@/utils/logger";

interface ProtectedRouteProps {
  children: ReactNode;
}

// Global auth state cache to prevent redundant checks
const authStateCache = {
  lastCheckTime: 0,
  isAuthenticated: false,
  expiresAt: 0,
  checkInProgress: false
};

// Cache TTL in milliseconds (reduced to 2 minutes for more frequent checks)
const AUTH_CACHE_TTL = 2 * 60 * 1000;

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const authChecked = useRef(false);
  const redirectAttempted = useRef(false);
  const authStateSubscription = useRef<{ subscription: { unsubscribe: () => void } } | null>(null);
  const initialSessionProcessed = useRef(false);
  const isServerSideAuth = useServerSideAuth();
  const currentPathRef = useRef(location.pathname);
  const isPwa = isPwaMode();
  const tokenValidityCheckerStarted = useRef(false);
  const pwaAuthSuccess = useRef(false); // Added ref to track immediate PWA success

  // Track route changes to detect navigation
  useEffect(() => {
    if (currentPathRef.current !== location.pathname) {
      logger.info(`Route changed from ${currentPathRef.current} to ${location.pathname}`);
      currentPathRef.current = location.pathname;

      // Check token validity on route change for PWA mode (make async)
      const checkRouteChange = async () => {
        // OPTIMIZATION: If we already have a valid Supabase session, we skip the FULL exchange
        // to prevent redirect loops. HOWEVER, we must still check for revocation in the background.
        const { data } = await supabase.auth.getSession();

        if (data.session) {
          logger.info("Route changed, valid session exists. Performing background revocation check...");
          checkRevocation();
          return;
        }

        if (isPwa && await shouldUsePwaTokenAuth()) { // Use await
          await checkTokenAuth(); // Use await
        }
      };
      checkRouteChange();
    }
  }, [location.pathname]);

  // Helper function for revocation check
  const checkRevocation = async () => {
    if (!isPwa) return;

    try {
      const result = await validateAuthToken();
      logger.info("Revocation check result:", result);

      if (result.revoked || (result.error && result.error === 'Token has been revoked')) {
        logger.warn("Background check detected revoked token! Logging out...");
        resetAuthCache();
        await clearAuthToken();
        toast.error("Your session has been revoked.");
        window.location.href = "/";
      }
    } catch (err) {
      logger.error("Background check error:", err);
    }
  };

  // Poll for revocation every 30 seconds
  useEffect(() => {
    if (!isPwa) return;

    const intervalId = setInterval(() => {
      logger.info("Polling for token revocation...");
      checkRevocation();
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [isPwa]);

  // Check if auth cache is still valid
  const isAuthCacheValid = () => {
    const now = Date.now();
    return (
      authStateCache.lastCheckTime > 0 &&
      now - authStateCache.lastCheckTime < AUTH_CACHE_TTL &&
      authStateCache.expiresAt > now / 1000
    );
  };

  // Reset auth cache (but keep the stored token - that's cleared separately when needed)
  const resetAuthCache = () => {
    authStateCache.lastCheckTime = 0;
    authStateCache.isAuthenticated = false;
    authStateCache.expiresAt = 0;
    // DO NOT clear the token here - resetAuthCache is called for temporary failures
    // Token should only be cleared explicitly when we know it's invalid/revoked
  };

  // Add token-based authentication for PWA
  const checkTokenAuth = async () => {
    // First check if we should attempt token auth (now async)
    const hasAuth = await shouldUsePwaTokenAuth();
    if (!hasAuth) return false;

    try {
      logger.info("ProtectedRoute: Checking token-based authentication");
      const { valid, userId, error, revoked } = await validateAuthToken();

      // Handle specific error cases
      if (revoked) {
        logger.info("ProtectedRoute: Token has been revoked");
        resetAuthCache();
        await clearAuthToken();
        toast.error("Your authentication has been revoked. Please login again.");
        window.location.href = "/";
        return false;
      }

      if (error === 'Token has been revoked') {
        logger.info("ProtectedRoute: Token has been revoked");
        await clearAuthToken();
        resetAuthCache();
        toast.error("Your authentication has been revoked. Please login again.");
        window.location.href = "/";
        return false;
      }

      if (valid && userId) {
        logger.info("ProtectedRoute: Valid token authentication for userId:", userId);

        // Attempt to establish a standard Supabase session using the validated token's user ID
        try {
          logger.info("Attempting to exchange PWA token user ID for Supabase session...");
          const exchangeResponse = await fetch(`${SUPABASE_URL}/functions/v1/token-auth-exchange`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${SUPABASE_PUBLISHABLE_KEY}`
            },
            body: JSON.stringify({
              token: await getAuthToken(),
              deviceFingerprint: await generateDeviceFingerprint()
            })
          });

          if (!exchangeResponse.ok) {
            const errorBody = await exchangeResponse.text();
            logger.error(`Token exchange failed with status ${exchangeResponse.status}: ${errorBody}`);
            resetAuthCache();
            toast.error("Failed to establish session after token validation. Please login again.");
            window.location.href = '/';
            return false;
          }

          const exchangeData = await exchangeResponse.json();

          if (exchangeData.success && exchangeData.authResponse?.access_token) {
            logger.info("Token exchange successful, setting session client-side.");
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: exchangeData.authResponse.access_token,
              refresh_token: exchangeData.authResponse.refresh_token,
            });

            if (sessionError) {
              logger.error("Failed to set session after token exchange:", sessionError);
              resetAuthCache();
              toast.error("Failed to establish session. Please try again.");
              window.location.href = '/';
              return false;
            }

            logger.info("Supabase session established successfully via token exchange.");

            // Start the token validity checker if not already started
            if (!tokenValidityCheckerStarted.current) {
              logger.info("Starting token validity checker from ProtectedRoute");
              startTokenValidityChecker();
              tokenValidityCheckerStarted.current = true;
            }

          } else {
            logger.error("Token exchange response did not contain required tokens:", exchangeData);
            resetAuthCache();
            toast.error("Failed to retrieve session tokens. Please login again.");
            window.location.href = '/';
            return false;
          }

        } catch (exchangeError) {
          logger.error("Error during token exchange or session setting:", exchangeError);
          resetAuthCache();
          toast.error("An error occurred during login. Please login again.");
          window.location.href = '/';
          return false;
        }

        return true;
      } else {
        logger.info(`ProtectedRoute: Token validation failed. Error: ${error}, Revoked: ${revoked}`);
        resetAuthCache();
        toast.error(error || "Authentication token invalid. Please login again.");
        window.location.href = '/';
        return false;
      }
    } catch (error) {
      logger.error("ProtectedRoute: Token validation error:", error);
      resetAuthCache();
      toast.error("An error occurred during authentication check. Please login again.");
      window.location.href = '/';
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    // Check current auth status once - Refactored logic
    const checkAuth = async () => {
      // Skip if already checked or unmounted
      if (authChecked.current || !isMounted) {
        return;
      }

      // If another auth check is in progress, wait a bit and check again
      if (authStateCache.checkInProgress) {
        logger.info("Another auth check is in progress, waiting...");
        setTimeout(checkAuth, 300);
        return;
      }

      authStateCache.checkInProgress = true; // Mark check as started

      try {
        // 1. ALWAYS check standard Supabase auth first (for both PWA and Web)
        // This is critical because for PWA, we might already have a session from a previous token exchange
        logger.info("Checking standard Supabase auth...");
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          logger.error("Protected route: Auth check failed:", error);
          // Don't return yet, we might still be able to auth via token if PWA
        }

        const hasSession = !!data.session;
        logger.info("Standard auth check result:", hasSession);

        if (hasSession) {
          // If we have a valid session, we are authenticated!
          if (isMounted) {
            setIsAuthenticated(true);
            authStateCache.isAuthenticated = true;
            authStateCache.lastCheckTime = Date.now();
            authStateCache.expiresAt = data.session?.expires_at || 0;
            setIsLoading(false);
            authChecked.current = true;

            // If we have a session, we don't need to check token auth
            authStateCache.checkInProgress = false;
            return;
          }
        }

        // 2. If NO session, and we are in PWA mode, try token auth
        if (isPwa && !hasSession) {
          logger.info("ProtectedRoute: No active session, PWA mode detected. Checking token auth.");
          const isTokenAuth = await checkTokenAuth(); // This function handles redirects on failure internally

          if (isTokenAuth) {
            // Token is valid, user is authenticated for PWA
            logger.info("ProtectedRoute: PWA token auth successful.");
            if (isMounted) {
              setIsAuthenticated(true);
              pwaAuthSuccess.current = true;
              authStateCache.isAuthenticated = true; // Update cache
              authStateCache.lastCheckTime = Date.now();
            }
          } else {
            // PWA token auth failed (redirect should have happened in checkTokenAuth)
            logger.info("ProtectedRoute: PWA token auth failed.");
            if (isMounted) {
              setIsAuthenticated(false);
              pwaAuthSuccess.current = false;
            }
          }
          // PWA check complete
          if (isMounted) {
            setIsLoading(false);
            authChecked.current = true;
          }
          authStateCache.checkInProgress = false;
          return; // Stop here for PWA mode
        }

        // 3. If NO session and NOT PWA (or PWA token check skipped), handle unauthenticated state
        if (isMounted) {
          setIsAuthenticated(false);
          setIsLoading(false);
          authChecked.current = true;

          // Only redirect if we haven't already tried to redirect
          if (!redirectAttempted.current) {
            redirectAttempted.current = true;
            if (isPwa) {
              logger.info("Redirecting to login (PWA, no session, token auth failed/skipped)");
            } else {
              logger.info("Redirecting to login (Web, no session)");
              toast.error("Please log in to access this page");
            }
            window.location.href = "/";
          }
        }

      } catch (err) {
        logger.error("Protected route: Auth check exception:", err);
        if (isMounted) {
          setIsAuthenticated(false);
          setIsLoading(false);
          authChecked.current = true;
          if (!redirectAttempted.current) {
            redirectAttempted.current = true;
            toast.error("Failed to check authentication. Please try again.");
            window.location.href = "/";
          }
        }
      } finally {
        authStateCache.checkInProgress = false; // Ensure this is always reset
      }
    };

    checkAuth();

    // Set up auth state change listener
    if (!authStateSubscription.current) {
      const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
        logger.info(`ProtectedRoute: Auth state changed: ${event}`, !!session);

        // Skip if component unmounted
        if (!isMounted) return;

        // Prevent duplicate INITIAL_SESSION handling
        if (event === 'INITIAL_SESSION') {
          if (initialSessionProcessed.current) {
            logger.info("ProtectedRoute: Ignoring duplicate INITIAL_SESSION event");
            return;
          }
          initialSessionProcessed.current = true;
        }

        // Update authentication state, BUT prioritize PWA success flag
        const isAuth = !!session;

        // If PWA auth has succeeded via the effect hook, trust that state unless explicitly signed out
        if (pwaAuthSuccess.current && event !== 'SIGNED_OUT') {
          if (!isAuthenticated) {
            logger.info("onAuthStateChange: Setting isAuthenticated to true based on pwaAuthSuccess ref.");
            setIsAuthenticated(true); // Ensure state aligns with PWA success ref
          }
          logger.info("onAuthStateChange: PWA auth succeeded, maintaining authenticated state.");
        } else {
          // Otherwise, update based on the session event
          logger.info(`onAuthStateChange: Updating isAuthenticated based on session event (${event}): ${isAuth}`);
          setIsAuthenticated(isAuth);
        }

        // Update global tracking cache based *only* on the session event
        // This cache reflects the actual Supabase session state, not the combined PWA state
        authStateCache.isAuthenticated = isAuth;
        authStateCache.lastCheckTime = Date.now();
        authStateCache.expiresAt = session?.expires_at || 0;

        // Ensure loading is false after the listener runs
        if (isLoading) setIsLoading(false);
        authChecked.current = true; // Mark that an auth check/event has occurred

        // Handle specific auth events for redirection (like SIGNED_OUT)
        if (event === 'SIGNED_OUT') {
          logger.info(`ProtectedRoute: Auth event ${event} detected, redirecting to login`);
          // Clear PWA success flag on sign out
          pwaAuthSuccess.current = false;
          // Stop token checker
          stopTokenValidityChecker();
          tokenValidityCheckerStarted.current = false;
          // Redirect to login page
          window.location.href = "/";
        } else if (event === 'TOKEN_REFRESHED' && !session) {
          logger.info("Token refresh failed, redirecting to login page");
          // Clear PWA success flag if refresh fails
          pwaAuthSuccess.current = false;
          window.location.href = "/";
        }
      });

      authStateSubscription.current = listener;
    }

    return () => {
      isMounted = false;

      // Unsubscribe from auth state changes
      if (authStateSubscription.current) {
        authStateSubscription.current.subscription.unsubscribe();
        authStateSubscription.current = null;
      }

      // Stop checker on unmount
      stopTokenValidityChecker();
    };
  }, [navigate, location.pathname, isServerSideAuth, isPwa]);

  // Show loading state with a simpler approach
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center">
          <div className="h-8 w-8 rounded-full border-4 border-t-primary border-r-primary border-b-muted border-l-muted animate-spin"></div>
          <p className="mt-4 text-muted-foreground">Verifying your login...</p>
        </div>
      </div>
    );
  }

  // Handle auth errors
  if (authError) {
    logger.error("Protected route auth error:", authError);
    return <Navigate to="/error" replace state={{ error: authError.message }} />;
  }

  // Final Render Logic:
  // 1. Show loader if still loading (Redundant check, but safe)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        {/* Loader JSX */}
        <div className="h-8 w-8 rounded-full border-4 border-t-primary border-r-primary border-b-muted border-l-muted animate-spin"></div>
        <p className="mt-4 text-muted-foreground">Verifying your login...</p>
      </div>
    );
  }

  // 2. Handle auth errors (if any occurred and were set)
  if (authError) {
    logger.error("Protected route auth error state:", authError);
    return <Navigate to="/error" replace state={{ error: authError.message }} />;
  }

  // 3. If loading is done and user is authenticated (use state OR the PWA success ref), render children
  // Check the ref first as it's set immediately in the effect for PWA success
  if (pwaAuthSuccess.current || isAuthenticated) {
    logger.info("Final Render: Authenticated, rendering children.");
    return <>{children}</>;
  }

  // 4. If loading is done and user is not authenticated (and PWA didn't succeed), redirect
  logger.info("Final Render: Not authenticated after loading, redirecting (fallback).");
  // Avoid infinite loops by checking redirectAttempted again
  if (!redirectAttempted.current) {
    redirectAttempted.current = true;
    return <Navigate to="/" replace />;
  }

  // If redirect already attempted, render null to avoid rendering children
  return null;
};

export default ProtectedRoute;
